import { FC, useContext } from "react";
import { GridLegacy as Grid, useMediaQuery, useTheme } from "@mui/material";

import { isAdmin } from "../../utility/utils";
import { IBookingRecordOptions, IOutboundFlightRecordOptions, IPreferences } from "../../logic/api";

import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";

import { SessionContext } from "../../utility/useSession";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "../../utility/feature-management";


interface IOutboundFlightRecordSettingsProps {
  onSave: (data: Partial<IPreferences>) => Promise<boolean>;
}

export const OutboundFlightRecordSettings: FC<IOutboundFlightRecordSettingsProps> = ({ onSave }) => {
  const session = useContext(SessionContext);
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useTranslation();
  const isRenameReleased = useReleaseManagement(ReleaseFlag.releaseRenameOutboundReturnFlightSettings);
  const isCollectionDeliveryMeetGreetReleased = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryMeetGreet);
  const isCollectionDeliveryPointsReleased = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryPoints);

  if (!session.current.site) {
    return null;
  }

  if (!isAdmin(session)) {
    return null;
  }

  const labelStyle = isSmallDevice
    ? { textAlign: "center" as const }
    : { paddingTop: 40 };

  const ensureOutboundFlightRecordOptions = (data: IPreferences): IOutboundFlightRecordOptions => {
    if (!data.outboundFlightRecordOptions) {
      data.outboundFlightRecordOptions = { properties: {} };
    }

    return data.outboundFlightRecordOptions;
  };

  const ensureBookingRecordOptions = (data: IPreferences): IBookingRecordOptions => {
    if (!data.bookingRecordOptions) {
      data.bookingRecordOptions = { properties: {} };
    }

    return data.bookingRecordOptions;
  };

  return (
    <Grid item xs={12}>
      <NotchedCard label={isCollectionDeliveryPointsReleased ? t("settings.arrivalTime") : isRenameReleased ? t("settings.outboundTime") : t("settings.outboundFlightRecOptions")} style={styles.card}>
        <EditableGroup<IPreferences>
          id="outboundFlightRecordOptions"
          editType={EditableGroupType.switchableEditing}
          data={session.current.site.preferences}
          onDataChanged={(data) => onSave({
            outboundFlightRecordOptions: data.outboundFlightRecordOptions,
            ...(isRenameReleased && { booking_outboundFlight_enabled: data.booking_outboundFlight_enabled }),
            ...((isCollectionDeliveryMeetGreetReleased || isCollectionDeliveryPointsReleased) && { bookingRecordOptions: data.bookingRecordOptions })
          })}
          testId="outbound-flight-record-options"
        >
          {isRenameReleased && (
            <EditableField<IPreferences, boolean>
              testId="settings-outbound-time-enabled-switch"
              inputType={EditableFieldTypes.switch}
              label={t("reports.enabled")}
              onProvide={(data) => Boolean(data.booking_outboundFlight_enabled)}
              onChange={(data, value) => { data.booking_outboundFlight_enabled = value; }}
            />
          )}

          {(!isRenameReleased || Boolean(session.current.site.preferences.booking_outboundFlight_enabled)) && (
            <>
              <Grid container>
                <Grid item sm={8} xs={12}>
                  <div style={labelStyle}><strong>{isRenameReleased ? t("flightLookup.labels.flightNumber") : t("booking.labels.outboundFlightNumber")}</strong></div>
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.visible")}
                    onProvide={(data) => Boolean(data.outboundFlightRecordOptions?.properties.flightNumber)}
                    onChange={(data, value) => ensureOutboundFlightRecordOptions(data).properties.flightNumber = value ? { required: false } : undefined}
                    testId="settings-outbound-flight-record-flight-number-visible-switch"
                  />
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.mandatory")}
                    onProvide={(data) => Boolean(data.outboundFlightRecordOptions?.properties.flightNumber?.required)}
                    onChange={(data, value) => ensureOutboundFlightRecordOptions(data).properties.flightNumber = { required: Boolean(value) }}
                    testId="settings-outbound-flight-record-flight-number-mandatory-switch"
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item sm={8} xs={12}>
                  <div style={labelStyle}><strong>{isRenameReleased ? t("booking.labels.destination") : t("booking.labels.outboundDestination")}</strong></div>
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.visible")}
                    onProvide={(data) => Boolean(data.outboundFlightRecordOptions?.properties.destination)}
                    onChange={(data, value) => ensureOutboundFlightRecordOptions(data).properties.destination = value ? { required: false } : undefined}
                    testId="settings-outbound-flight-record-destination-visible-switch"
                  />
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.mandatory")}
                    onProvide={(data) => Boolean(data.outboundFlightRecordOptions?.properties.destination?.required)}
                    onChange={(data, value) => ensureOutboundFlightRecordOptions(data).properties.destination = { required: Boolean(value) }}
                    testId="settings-outbound-flight-record-destination-mandatory-switch"
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item sm={8} xs={12}>
                  <div style={labelStyle}><strong>{isRenameReleased ? t("flightLookup.labels.terminal") : t("booking.labels.outboundTerminal")}</strong></div>
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.visible")}
                    onProvide={(data) => Boolean(data.outboundFlightRecordOptions?.properties.terminal)}
                    onChange={(data, value) => ensureOutboundFlightRecordOptions(data).properties.terminal = value ? { required: false } : undefined}
                    testId="settings-outbound-flight-record-terminal-visible-switch"
                  />
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.mandatory")}
                    onProvide={(data) => Boolean(data.outboundFlightRecordOptions?.properties.terminal?.required)}
                    onChange={(data, value) => ensureOutboundFlightRecordOptions(data).properties.terminal = { required: Boolean(value) }}
                    testId="settings-outbound-flight-record-terminal-mandatory-switch"
                  />
                </Grid>
              </Grid>

              {isCollectionDeliveryPointsReleased && <Grid container>
                <Grid item sm={8} xs={12}>
                  <div style={labelStyle}><strong>{t("booking.labels.collectionPoint")}</strong></div>
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.visible")}
                    onProvide={(data) => Boolean(data.bookingRecordOptions?.properties.collectionPoint)}
                    onChange={(data, value) => ensureBookingRecordOptions(data).properties.collectionPoint = value ? { required: false } : undefined}
                    testId="settings-outbound-flight-record-collection-point-visible-switch"
                  />
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.mandatory")}
                    onProvide={(data) => Boolean(data.bookingRecordOptions?.properties.collectionPoint?.required)}
                    onChange={(data, value) => ensureBookingRecordOptions(data).properties.collectionPoint = { required: Boolean(value) }}
                    testId="settings-outbound-flight-record-collection-point-mandatory-switch"
                  />
                </Grid>
              </Grid>}

              {isCollectionDeliveryMeetGreetReleased && <Grid container>
                <Grid item sm={8} xs={12}>
                  <div style={labelStyle}><strong>{t("booking.labels.confirmedArrivalDate")}</strong></div>
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.visible")}
                    onProvide={(data) => Boolean(data.bookingRecordOptions?.properties.confirmedArrivalDate)}
                    onChange={(data, value) => ensureBookingRecordOptions(data).properties.confirmedArrivalDate = value}
                    testId="settings-outbound-flight-record-confirmed-arrival-date-visible-switch"
                  />
                </Grid>
              </Grid>}
            </>
          )}
        </EditableGroup>
      </NotchedCard>
    </Grid>
  );
};
